import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lightning, CircleNotch, Check, X, VideoCamera, FilmStrip, Sparkle, Play, Download, ArrowRight, CalendarBlank, ArrowCounterClockwise, Swap, Eye, Trash } from "@phosphor-icons/react";
import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";
import ContentCalendar from "@/pages/ContentCalendar";

const BulkCreate = () => {
  const [tab, setTab] = useState("create");
  const [autoCount, setAutoCount] = useState(7);
  const [lifestyleCount, setLifestyleCount] = useState(0);
  const [includeCaptions, setIncludeCaptions] = useState(false);
  const [includeBroll, setIncludeBroll] = useState(false);
  const [topics, setTopics] = useState("");
  const [creating, setCreating] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState(null);
  const [imageCount, setImageCount] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rerunningJob, setRerunningJob] = useState(null);
  const [swapJobId, setSwapJobId] = useState(null);
  const [twinImages, setTwinImages] = useState([]);
  // Provider preference — same picker as AutoCreate / ScriptStudio.
  const [talkingPhotoProvider, setTalkingPhotoProvider] = useState("auto");
  const [talkingPhotoResolution, setTalkingPhotoResolution] = useState("480p");

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bulk-create/batches`);
      setBatches(res.data.batches || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchImageCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-twin/images`);
      setImageCount(res.data.images?.length || 0);
      setTwinImages(res.data.images || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchBatches();
      fetchImageCount();
    }, 0);
    return () => clearTimeout(t);
  }, [fetchBatches, fetchImageCount]);

  // Poll active batch
  useEffect(() => {
    if (!activeBatchId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/bulk-create/batch/${activeBatchId}`);
        setBatchDetail(res.data);
        const status = res.data.batch?.status;
        if (status === "completed" || status === "partial" || status === "failed") {
          setCreating(false);
          fetchBatches();
          clearInterval(interval);
        }
      } catch (e) { console.error(e); }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeBatchId, fetchBatches]);

  const handleStart = async () => {
    if (imageCount === 0) {
      setError("Upload AI Twin images first (AI Twin Gallery → Upload)");
      return;
    }
    setCreating(true);
    setError(null);
    setBatchDetail(null);
    try {
      const topicList = topics.trim() ? topics.split("\n").map(t => t.trim()).filter(Boolean) : [];
      const res = await axios.post(`${API}/bulk-create/start`, {
        auto_create_count: autoCount,
        lifestyle_count: lifestyleCount,
        include_captions: includeCaptions,
        include_broll: includeBroll,
        topics: topicList,
        // Default the bulk pipeline to Talking Photo "realistic" mode so
        // batches maximize face likeness — user explicitly chose Talking
        // Photo as the primary video model across the platform.
        talking_photo_mode: "realistic",
        talking_photo_provider: talkingPhotoProvider,
        talking_photo_resolution: talkingPhotoResolution,
      });
      setActiveBatchId(res.data.batch_id);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start batch");
      setCreating(false);
    }
  };

  const totalVideos = autoCount + lifestyleCount;
  const completedJobs = batchDetail?.jobs?.filter(j => j.status === "completed") || [];
  const failedJobs = batchDetail?.jobs?.filter(j => j.status === "failed") || [];
  const processingIndex = batchDetail?.batch?.current_index ?? 0;

  const handleRerun = async (jobId, newImageId = null) => {
    setRerunningJob(jobId);
    try {
      const body = newImageId ? { image_id: newImageId } : {};
      await axios.post(`${API}/bulk-create/rerun/${jobId}`, body, { headers: getAuthHeaders() });
      // Refresh batch detail
      if (activeBatchId) {
        const res = await axios.get(`${API}/bulk-create/batch/${activeBatchId}`);
        setBatchDetail(res.data);
        setCreating(true);
      }
      setSwapJobId(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Rerun failed");
    } finally {
      setRerunningJob(null);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!batchId) return;
    if (!window.confirm("Delete this batch and all its videos? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/bulk-create/batch/${batchId}`, { headers: getAuthHeaders() });
      setBatchDetail(null);
      setActiveBatchId(null);
      setCreating(false);
      fetchBatches();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-create-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-zinc-900">Bulk Create</h2>
          <p className="text-zinc-500 text-sm mt-1">Generate multiple videos at once — AI rotates your twin images and auto-picks topics</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-200 pb-1">
        <button
          onClick={() => setTab("create")}
          data-testid="bulk-tab-create"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
            tab === "create" ? "text-[#E91E8C] border-b-2 border-[#E91E8C] -mb-[3px]" : "text-zinc-500 hover:text-zinc-700"
          }`}
        >
          <Lightning size={18} />
          Create
        </button>
        <button
          onClick={() => setTab("calendar")}
          data-testid="bulk-tab-calendar"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
            tab === "calendar" ? "text-[#E91E8C] border-b-2 border-[#E91E8C] -mb-[3px]" : "text-zinc-500 hover:text-zinc-700"
          }`}
        >
          <CalendarBlank size={18} />
          Calendar
        </button>
      </div>

      {tab === "calendar" && (
        <ContentCalendar batches={batches} onSwitchToCreate={() => setTab("create")} />
      )}

      {tab === "create" && (
        <>
      {/* Config Panel */}
      {!creating && !batchDetail?.batch && (
        <div className="space-y-4">
          {/* Video Counts */}
          <div className="grid grid-cols-2 gap-4">
            <div className="card p-4 space-y-3">
              <div className="flex items-center gap-2">
                <VideoCamera size={20} weight="duotone" className="text-[#E91E8C]" />
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Talking Photo Videos</label>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#E91E8C]/10 text-[#E91E8C]">PRIMARY</span>
              </div>
              <p className="text-xs text-zinc-400">Script, voice clone & lip-synced talking-photo video — best likeness</p>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={15}
                  value={autoCount}
                  onChange={(e) => setAutoCount(Number(e.target.value))}
                  className="flex-1 accent-[#E91E8C]"
                  data-testid="auto-count-slider"
                />
                <span className="text-xl font-bold text-zinc-900 w-8 text-center" data-testid="auto-count-value">{autoCount}</span>
              </div>
            </div>

            <div className="card p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FilmStrip size={20} weight="duotone" className="text-zinc-400" />
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Lifestyle Videos</label>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">OPTIONAL</span>
              </div>
              <p className="text-xs text-zinc-400">Prompted motion clips with rotating vibes — no audio / no script</p>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={lifestyleCount}
                  onChange={(e) => setLifestyleCount(Number(e.target.value))}
                  className="flex-1 accent-[#E91E8C]"
                  data-testid="lifestyle-count-slider"
                />
                <span className="text-xl font-bold text-zinc-900 w-8 text-center" data-testid="lifestyle-count-value">{lifestyleCount}</span>
              </div>
            </div>
          </div>

          {/* Video Model picker */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Video Model</label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setTalkingPhotoProvider("auto")}
                className={`p-2.5 rounded-lg border text-left transition-all ${talkingPhotoProvider === "auto" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}
                data-testid="bulk-provider-auto"
                title="Pruna for talking-photo, VEED for lip-sync — best value"
              >
                <p className="text-xs font-bold text-zinc-900 leading-tight">Auto ✨</p>
                <p className="text-[10px] text-zinc-500 leading-tight mt-0.5">Best-value engine</p>
              </button>
              <button
                type="button"
                onClick={() => setTalkingPhotoProvider("heygen")}
                className={`p-2.5 rounded-lg border text-left transition-all ${talkingPhotoProvider === "heygen" ? "border-violet-500 bg-violet-50" : "border-zinc-200 hover:border-zinc-300"}`}
                data-testid="bulk-provider-heygen"
                title="HeyGen HD (Avatar IV) — premium quality. Uses extra video credits per clip."
              >
                <div className="flex items-center gap-1">
                  <p className="text-xs font-bold text-zinc-900 leading-tight">💎 HeyGen</p>
                  <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-violet-200 text-violet-800">HD</span>
                </div>
                <p className="text-[10px] text-zinc-500 leading-tight mt-0.5">Best quality · uses credits</p>
              </button>
              <button
                type="button"
                onClick={() => setTalkingPhotoProvider("veed")}
                className={`p-2.5 rounded-lg border text-left transition-all ${talkingPhotoProvider === "veed" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                data-testid="bulk-provider-veed"
              >
                <p className="text-xs font-bold text-zinc-900 leading-tight">🎤 VEED Lipsync</p>
                <p className="text-[10px] text-zinc-500 leading-tight mt-0.5">~1 min · lip-sync only</p>
              </button>
            </div>
            {talkingPhotoProvider === "heygen" && (
              <p className="text-[10px] leading-tight text-violet-800 bg-violet-50 border border-violet-200 rounded p-1.5" data-testid="bulk-heygen-note">
                💎 HeyGen HD is premium — each clip in this batch uses extra video credits (scales with length). Make sure your HeyGen wallet is funded.
              </p>
            )}
          </div>

          {/* Options */}
          <div className="card p-4 space-y-3">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Options</label>
            <div className="flex gap-4">
              <button
                onClick={() => setIncludeCaptions(!includeCaptions)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                  includeCaptions ? "border-[#E91E8C] bg-[#E91E8C]/5 text-[#E91E8C]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"
                }`}
                data-testid="bulk-toggle-captions"
              >
                {includeCaptions ? <Check size={16} /> : null}
                AI Captions
              </button>
              <button
                onClick={() => setIncludeBroll(!includeBroll)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                  includeBroll ? "border-[#E91E8C] bg-[#E91E8C]/5 text-[#E91E8C]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"
                }`}
                data-testid="bulk-toggle-broll"
              >
                {includeBroll ? <Check size={16} /> : null}
                AI B-Roll
              </button>
            </div>
          </div>

          {/* Topics */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Topics</label>
              <span className="text-[10px] text-zinc-400">Leave empty for AI-generated topics</span>
            </div>
            <textarea
              value={topics}
              onChange={(e) => setTopics(e.target.value)}
              placeholder={"Enter one topic per line, or leave blank for AI to generate topics based on your brand...\n\nExample:\n3 skincare mistakes ruining your glow\nWhy your morning routine is holding you back\nThe secret ingredient top brands don't tell you"}
              className="input-field text-sm min-h-[100px] resize-none"
              data-testid="bulk-topics-input"
            />
          </div>

          {/* Summary & Start */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-lg font-bold text-zinc-900">{totalVideos} videos will be created</p>
                <p className="text-sm text-zinc-400">
                  {autoCount} talking-photo + {lifestyleCount} lifestyle
                  {imageCount > 0 ? ` • rotating through ${imageCount} AI twin images` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-400">Estimated time</p>
                <p className="text-sm font-semibold text-zinc-700">~{Math.ceil(totalVideos * 4)} - {Math.ceil(totalVideos * 7)} min</p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-3" data-testid="bulk-error">
                {error}
              </div>
            )}

            <button
              onClick={handleStart}
              disabled={totalVideos === 0}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base"
              data-testid="bulk-start-btn"
            >
              <Lightning size={20} weight="fill" />
              Create {totalVideos} Videos
            </button>
          </div>
        </div>
      )}

      {/* Active Batch Progress */}
      {(creating || batchDetail?.batch) && batchDetail && (
        <div className="space-y-4">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-lg font-bold text-zinc-900">
                  {batchDetail.batch.status === "completed" ? "Batch Complete!" :
                   batchDetail.batch.status === "partial" ? "Batch Partial" :
                   batchDetail.batch.status === "failed" ? "Batch Failed" :
                   "Creating videos..."}
                </p>
                <p className="text-sm text-zinc-400">
                  {completedJobs.length} done • {failedJobs.length} failed • {batchDetail.batch.total_jobs - completedJobs.length - failedJobs.length} remaining
                </p>
              </div>
              {creating && <CircleNotch size={24} className="animate-spin text-[#E91E8C]" />}
              {batchDetail.batch.status === "completed" && <Check size={24} className="text-green-500" weight="bold" />}
              {!creating && batchDetail.batch.status !== "processing" && batchDetail.batch.status !== "creating" && (
                <button
                  onClick={() => handleDeleteBatch(batchDetail.batch.id)}
                  data-testid="delete-batch-btn"
                  className="ml-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  title="Delete this batch and all its videos"
                >
                  <Trash size={15} /> Delete batch
                </button>
              )}
            </div>

            {/* Progress bar */}
            <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#E91E8C] to-[#ff6ec7] transition-all duration-500"
                style={{ width: `${Math.round(((completedJobs.length + failedJobs.length) / batchDetail.batch.total_jobs) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-zinc-400 mt-1 text-right">
              {Math.round(((completedJobs.length + failedJobs.length) / batchDetail.batch.total_jobs) * 100)}%
            </p>
          </div>

          {/* Job list */}
          <div className="space-y-3">
            {(batchDetail.jobs || []).map((job, idx) => (
              <div
                key={job.id}
                className={`card overflow-hidden ${
                  job.status === "completed" ? "border-l-4 border-l-green-400" :
                  job.status === "failed" ? "border-l-4 border-l-red-400" :
                  job.status === "processing" ? "border-l-4 border-l-[#E91E8C]" :
                  ""
                }`}
                data-testid={`bulk-job-${job.id}`}
              >
                {/* Job Header Row */}
                <div className="p-3 flex items-center gap-3">
                  {/* AI Twin thumbnail */}
                  {(job.image_thumbnail || job.image_filename) ? (
                    <img
                      src={`${BACKEND_URL}/api/ai-twin/images/${job.image_thumbnail || job.image_filename}`}
                      alt="AI Twin"
                      className="w-10 h-10 rounded-lg object-cover border border-zinc-200 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-400 shrink-0">
                      {idx + 1}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {job.type === "auto_create" ? (
                        <VideoCamera size={14} className="text-[#E91E8C]" />
                      ) : (
                        <FilmStrip size={14} className="text-purple-500" />
                      )}
                      <p className="text-sm font-medium text-zinc-800 truncate">
                        {job.type === "auto_create" ? job.topic : `${job.vibe} • ${job.style}`}
                      </p>
                    </div>
                    <p className="text-[10px] text-zinc-400 truncate">{job.image_name || "AI Twin"}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {job.status === "completed" && (
                      <>
                        <Check size={14} className="text-green-500" weight="bold" />
                        {job.output_url && (
                          <button
                            onClick={() => setPreviewUrl(previewUrl === job.id ? null : job.id)}
                            className={`p-1.5 rounded-lg transition-all ${previewUrl === job.id ? "bg-[#E91E8C]/10 text-[#E91E8C]" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-500"}`}
                            title="Preview video"
                            data-testid={`preview-${job.id}`}
                          >
                            <Play size={14} weight="fill" />
                          </button>
                        )}
                        {job.output_url && (
                          <a href={job.output_url} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-zinc-100 rounded-lg hover:bg-zinc-200 text-zinc-500 transition-all" title="Download">
                            <Download size={14} />
                          </a>
                        )}
                        <button
                          onClick={() => setSwapJobId(swapJobId === job.id ? null : job.id)}
                          className="p-1.5 bg-zinc-100 rounded-lg hover:bg-zinc-200 text-zinc-500 transition-all"
                          title="Swap twin & rerun"
                          data-testid={`swap-${job.id}`}
                        >
                          <Swap size={14} />
                        </button>
                      </>
                    )}
                    {job.status === "failed" && (
                      <>
                        <span className="text-[10px] text-red-500 max-w-[80px] truncate" title={job.error}>{(job.error || "Failed").split("—")[0]}</span>
                        <button
                          onClick={() => handleRerun(job.id)}
                          disabled={rerunningJob === job.id}
                          className="p-1.5 bg-red-50 rounded-lg hover:bg-red-100 text-red-500 transition-all"
                          title="Retry"
                          data-testid={`rerun-${job.id}`}
                        >
                          {rerunningJob === job.id ? <CircleNotch size={14} className="animate-spin" /> : <ArrowCounterClockwise size={14} />}
                        </button>
                        <button
                          onClick={() => setSwapJobId(swapJobId === job.id ? null : job.id)}
                          className="p-1.5 bg-zinc-100 rounded-lg hover:bg-zinc-200 text-zinc-500 transition-all"
                          title="Swap twin & rerun"
                        >
                          <Swap size={14} />
                        </button>
                      </>
                    )}
                    {job.status === "processing" && <CircleNotch size={16} className="animate-spin text-[#E91E8C]" />}
                    {job.status === "queued" && <span className="text-[10px] text-zinc-400">Queued</span>}
                  </div>
                </div>

                {/* Inline Video Preview */}
                {previewUrl === job.id && job.output_url && (
                  <div className="px-3 pb-3">
                    <div className="rounded-xl overflow-hidden bg-black border border-zinc-200" data-testid={`video-preview-${job.id}`}>
                      <video src={job.output_url} controls autoPlay className="w-full max-h-[400px]" />
                    </div>
                  </div>
                )}

                {/* Image Swap Picker */}
                {swapJobId === job.id && (
                  <div className="px-3 pb-3">
                    <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200" data-testid={`swap-picker-${job.id}`}>
                      <p className="text-xs font-semibold text-zinc-600 mb-2">Select a different AI Twin image and rerun:</p>
                      <div className="flex gap-2 flex-wrap">
                        {twinImages.map(img => (
                          <button
                            key={img.id}
                            onClick={() => handleRerun(job.id, img.id)}
                            disabled={rerunningJob === job.id}
                            className={`w-14 h-14 rounded-lg overflow-hidden border-2 hover:border-[#E91E8C] transition-all ${
                              job.image_id === img.id ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/20" : "border-zinc-200"
                            }`}
                            data-testid={`swap-img-${img.id}`}
                          >
                            <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setSwapJobId(null)} className="text-xs text-zinc-400 mt-2 hover:text-zinc-600">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!creating && (
            <button
              onClick={() => { setBatchDetail(null); setActiveBatchId(null); fetchBatches(); }}
              className="text-sm text-[#E91E8C] font-medium hover:underline"
              data-testid="bulk-new-batch-btn"
            >
              Create another batch
            </button>
          )}
        </div>
      )}

      {/* Past Batches */}
      {!creating && !batchDetail?.batch && batches.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Past Batches</p>
          {batches.map((batch) => (
            <button
              key={batch.id}
              onClick={async () => {
                const res = await axios.get(`${API}/bulk-create/batch/${batch.id}`);
                setBatchDetail(res.data);
                setActiveBatchId(batch.id);
              }}
              className="card p-3 w-full text-left flex items-center justify-between hover:bg-zinc-50 transition-all"
              data-testid={`past-batch-${batch.id}`}
            >
              <div>
                <p className="text-sm font-medium text-zinc-800">
                  {batch.auto_create_count} talking-photo + {batch.lifestyle_count} lifestyle
                </p>
                <p className="text-xs text-zinc-400">{new Date(batch.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  batch.status === "completed" ? "bg-green-100 text-green-700" :
                  batch.status === "partial" ? "bg-amber-100 text-amber-700" :
                  batch.status === "failed" ? "bg-red-100 text-red-700" :
                  "bg-zinc-100 text-zinc-500"
                }`}>
                  {batch.completed_count || 0}/{batch.total_jobs} done
                </span>
                <ArrowRight size={14} className="text-zinc-400" />
              </div>
            </button>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default BulkCreate;
